import { type ChangeEvent, type FC, type KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";

import { cx } from "@emotion/css";
import { PopoverPlacementTypes } from "@snapchat/snap-design-system";
import { PencilIcon } from "@snapchat/snap-design-system-icons";

import { Flexbox } from "../../core/components";
import { useInputState } from "../../core/validation";
import { GroupsRestrictions } from "../../groups/model";
import { KeyboardKey } from "../model";

import { ActionDisabledTooltip } from "./ActionDisabledTooltip";
import { EditIconButton } from "./EditIconButton";
import { inputStyles } from "./EditableText.styles";

interface EditableInputProps {
    initialValue: string;
    className: string;
    isDisabled: boolean;
    hasPermission: boolean;
    onNewValue: (newValue: string) => void;
}

export const EditableText: FC<EditableInputProps> = ({
    initialValue,
    className,
    isDisabled,
    onNewValue,
    hasPermission,
}) => {
    const [isEditMode, setEditMode] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const {
        inputProps: { onChange, value },
        isValid,
    } = useInputState<string>({
        error: "Name field is required.",
        defaultValue: initialValue,
        validator: (newValue) => Boolean(newValue?.trim()),
    });

    const toggleEditMode = useCallback(() => {
        setEditMode((isEnabled) => !isEnabled);
    }, []);

    const applyNewValueIfValid = useCallback(() => {
        const newValue = value?.trim();
        if (isValid && newValue !== initialValue) {
            onNewValue(newValue);
        } else {
            onChange(initialValue);
        }
        toggleEditMode();
    }, [initialValue, isValid, onChange, onNewValue, toggleEditMode, value]);

    const exitEditMode = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            switch (event.key as KeyboardKey) {
                case KeyboardKey.Enter:
                    applyNewValueIfValid();
                    break;
                case KeyboardKey.Esc:
                case KeyboardKey.Escape:
                    toggleEditMode();
                    onChange(initialValue);
                    break;
            }
        },
        [applyNewValueIfValid, initialValue, onChange, toggleEditMode],
    );

    const changeValue = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            onChange(event.target.value);
        },
        [onChange],
    );

    useEffect(() => {
        onChange(initialValue);
    }, [initialValue, onChange]);

    useEffect(() => {
        if (isEditMode && inputRef.current) {
            inputRef.current.focus();

            const refValue = inputRef.current.value;
            if (refValue) {
                inputRef.current.setSelectionRange(refValue.length, refValue.length);
            }
        }
    }, [isEditMode]);

    return (
        <Flexbox alignItems="flex-start">
            {isEditMode ? (
                <input
                    ref={inputRef}
                    className={cx(inputStyles, className)}
                    value={value}
                    readOnly={!isEditMode}
                    onChange={changeValue}
                    onKeyUp={exitEditMode}
                    onBlur={applyNewValueIfValid}
                    maxLength={GroupsRestrictions.maxGroupNameLength}
                />
            ) : (
                <span className={className}>{value}</span>
            )}
            <ActionDisabledTooltip hasPermission={hasPermission} placement={PopoverPlacementTypes.TOP_LEFT}>
                <EditIconButton
                    show={!isEditMode}
                    icon={<PencilIcon />}
                    onClick={toggleEditMode}
                    isDisabled={isDisabled}
                />
            </ActionDisabledTooltip>
        </Flexbox>
    );
};
